import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import type { GccCombinableScenario, GccDashboardData, Project } from '@shared/types'
import { combinedDashboardLabel, mergeCombinedDashboard } from '@shared/gcc/combine'
import { GccErrorDetails } from './GccErrorDetails'
import { Badge } from './GccSourcingModel'
import { GCC_BADGE, GCC_SCENARIO_BADGE } from './gccExecutiveStyle'

interface Props {
  gcc: GccDashboardData
  /** The linked Retained (HQ + Markets) analysis, if one exists. */
  retained?: Project
  /** Linked Medium / Low analyses, keyed by scenario. */
  scenarioProjects: Partial<Record<GccCombinableScenario, Project>>
  /** True when a linked analysis has a dashboard and no job running. */
  isReady: (project: Project | undefined) => boolean
  /** Scenarios that already have a combined dashboard. */
  existing?: GccCombinableScenario[]
  /** Rebuild mode: preselects the scenario and relabels the action. */
  rebuildScenario?: GccCombinableScenario
  combine: (scenario: GccCombinableScenario) => Promise<{ projectId: string }>
  onCombined: (projectId: string, scenario: GccCombinableScenario) => void
  onClose: () => void
}

const COMBINABLE: GccCombinableScenario[] = ['Medium', 'Low']

const fmt = (n: number) => Math.round(n).toLocaleString('en-US')

export default function GccCombineModal({
  gcc, retained, scenarioProjects, isReady, existing = [], rebuildScenario, combine, onCombined, onClose,
}: Props) {
  const retainedReady = isReady(retained)
  const readyScenarios = COMBINABLE.filter(s => retainedReady && isReady(scenarioProjects[s]))
  const [scenario, setScenario] = useState<GccCombinableScenario | null>(rebuildScenario ?? readyScenarios[0] ?? null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = useMemo(() => {
    const offshore = scenario ? scenarioProjects[scenario] : undefined
    if (!scenario || !retained?.data || !offshore?.data) return null
    try {
      return mergeCombinedDashboard(retained.data, offshore.data, gcc, scenario).summary
    } catch {
      return null
    }
  }, [gcc, retained, scenarioProjects, scenario])

  const unavailableReason = (s: GccCombinableScenario): string | null => {
    if (!retained) return 'Run the Retained (HQ + Markets) analysis first.'
    if (!retainedReady) return 'Waiting for the Retained (HQ + Markets) analysis to finish.'
    if (!scenarioProjects[s]) return `Run the ${s} analysis first.`
    if (!isReady(scenarioProjects[s])) return `Waiting for the ${s} analysis to finish.`
    return null
  }

  const handleCombine = async () => {
    if (!scenario || running) return
    setRunning(true)
    setError(null)
    try {
      const { projectId } = await combine(scenario)
      onCombined(projectId, scenario)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build the combined dashboard.')
      setRunning(false)
    }
  }

  const isRebuild = !!rebuildScenario
  const replacing = scenario !== null && existing.includes(scenario)

  return (
    <div
      className="gx-modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div role="dialog" aria-label={isRebuild ? 'Rebuild combined dashboard' : 'Combine TN analyses'} className="gx-modal">
        <div className="gx-modal-head">
          <div>
            <p className="gx-modal-eyebrow">GCC · TN Analysis</p>
            <h2 className="gx-modal-title">
              {isRebuild ? 'Rebuild Combined Dashboard' : 'Combine TN Analyses'}
            </h2>
            <p className="gx-modal-sub">Assembled from existing results — no new AI run.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="gx-modal-close">✕</button>
        </div>

        <div className="gx-modal-body">
          <div className="gx-note">
            <p className="gx-note-title">How the roll-up avoids double counting</p>
            <p>
              Medium and Low analyses already include the HQ + Markets workforce. The combined dashboard takes
              HQ + Markets roles <b>only from the Retained analysis</b>, and from the chosen scenario <b>only the
              GCC-eligible residual roles</b> that stay onshore. Nobody is counted twice.
            </p>
          </div>

          <p className="gx-label">Pair Retained with</p>
          <div className="gx-opts">
            {COMBINABLE.map(s => {
              const reason = unavailableReason(s)
              const disabled = reason !== null || (isRebuild && s !== rebuildScenario)
              const selected = scenario === s
              const badge = disabled ? GCC_BADGE.gray : GCC_SCENARIO_BADGE[s]
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => !disabled && setScenario(s)}
                  disabled={disabled}
                  aria-pressed={selected}
                  className={`gx-opt${disabled ? ' is-disabled' : selected ? ' is-selected' : ''}`}
                  style={{ '--opt': badge.text } as CSSProperties}
                >
                  <div className="gx-opt-row">
                    <Badge label={s} style={badge} />
                    <span className="gx-opt-title">{combinedDashboardLabel(s)}</span>
                    {existing.includes(s) && <span className="gx-status ml-auto">Exists</span>}
                  </div>
                  {reason && <p className="gx-opt-desc">{reason}</p>}
                </button>
              )
            })}
            <div className="gx-opt is-disabled" aria-disabled="true">
              <div className="gx-opt-row">
                <Badge label="High" style={GCC_BADGE.gray} />
                <span className="gx-opt-title">Not combinable</span>
              </div>
              <p className="gx-opt-desc">
                High has no GCC-eligible residual — its workforce is the same HQ + Markets population, so combining it would just repeat the Retained analysis.
              </p>
            </div>
          </div>

          {preview && scenario && (
            <div className="gx-kv" aria-label="Combined headcount preview">
              <div className="gx-kv-row"><span>Retained (HQ + Markets)</span><span className="gx-kv-n">{fmt(preview.retainedFte)} FTE</span></div>
              <div className="gx-kv-row"><span>GCC-eligible residual ({scenario})</span><span className="gx-kv-n">+ {fmt(preview.residualFte)} FTE</span></div>
              <div className="gx-kv-row is-total"><span>Combined total</span><span className="gx-kv-n">{fmt(preview.totalFte)} FTE</span></div>
              {preview.droppedRetainedRoles.length > 0 && (
                <p className="gx-kv-row gx-cap">
                  {preview.droppedRetainedRoles.length} HQ + Markets role{preview.droppedRetainedRoles.length === 1 ? '' : 's'} from the {scenario} analysis left out (covered by Retained).
                </p>
              )}
              {preview.unmatchedRoles.length > 0 && (
                <p className="gx-kv-row text-xs text-amber-800 bg-amber-50">
                  {preview.unmatchedRoles.length} role{preview.unmatchedRoles.length === 1 ? '' : 's'} in the {scenario} analysis could not be matched to the GCC design and will not be included: {preview.unmatchedRoles.join(', ')}.
                </p>
              )}
            </div>
          )}

          {replacing && (
            <p className="gx-cap">This replaces the existing {combinedDashboardLabel(scenario)} with one built from the current results.</p>
          )}

          {error && <div className="text-center"><GccErrorDetails message={error} /></div>}
        </div>

        <div className="gx-modal-foot">
          <button onClick={onClose} className="gx-btn">Cancel</button>
          <button
            onClick={() => void handleCombine()}
            disabled={!scenario || running || unavailableReason(scenario) !== null}
            className="gx-btn-primary"
          >
            {running ? 'Building…' : isRebuild ? 'Rebuild →' : 'Build Combined Dashboard →'}
          </button>
        </div>
      </div>
    </div>
  )
}
